import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
// @ts-ignore
import { Component, EventEmitter, Input, Output, inject, signal, OnInit } from '@angular/core';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { SankhyaGatewayService } from "../../services/sankhya-gateway.service"; 

interface ParceiroSugestao {
    codparc: string | number;
    nomeparc: string;
    docparc: string;
    tipoBusca: 'CLIENTE';
}

@Component({
    selector: 'app-cadastro-aeronave',
    standalone: true,
    imports: [LoadingIndicatorComponent, CommonModule, ReactiveFormsModule, FormsModule],
    templateUrl: './cadastro-aeronave.component.html',
    styleUrls: ['./cadastro-aeronave.component.scss']
})
export class CadastroAeronaveComponent implements OnInit {
    private fb = inject(FormBuilder);
    private sankhyaGateway = inject(SankhyaGatewayService);

    /** Prefixo da aeronave a abrir em edicao (duplo clique na lista). null = nova aeronave. */
    @Input() prefixoEdicao: string | null = null;
    /** Salvou com sucesso (sem avisos): volta para a lista. */
    @Output() salvo = new EventEmitter<void>();
    @Output() voltar = new EventEmitter<void>();

    salvando = signal(false);
    toast = signal<{ mensagem: string, tipo: 'success' | 'danger' | 'warning' | 'info' } | null>(null);
    buscandoPrefixo = signal(false);
    ultimoPrefixoConsultado: string = '';

    // Sinais de Controle para Autocomplete de Parceiros
    parceiros = signal<any[]>([]); 
    dropdownParceiroAberto = signal(false);
    filtroParceiro = signal<string>('');
    inputParceiroDisplay = signal<string>('');
    indiceFocoAtual = signal<number>(-1);

    // Sinais de Controle para a listagem/vínculos inferiores de parceiros
    dropdownVinculoAberto = signal<number>(-1);
    debounceConsulta: any;

    // Controles para Autocomplete de Prefixo
    dropdownPrefixoAberto = signal(false);
    sugestoesPrefixo = signal<any[]>([]);
    private debouncePrefixoTimer: ReturnType<typeof setTimeout> | null = null;

    parceirosVinculadosParaExcluir: any[] = [];

    // ==========================================
    // ESTADOS E CONTROLES: BUSCA GERAL (INLINE)
    // ==========================================
    buscaColapsada = false;
    dropdownBuscaAeronaveGeralAberto = false;
    filtroBuscaAeronaveGeral = '';
    statusBuscaAeronaveGeral = signal('Digite ao menos 2 caracteres para pesquisar...');
    listaAeronavesGeralSugeridas = signal<any[]>([]);
    private buscaAeronaveGeralTimer: ReturnType<typeof setTimeout> | null = null;

    aeronaveForm: FormGroup = this.fb.group({
        codAero: [null],
        prefixo: ['', [Validators.required, Validators.maxLength(10)]],
        codparc: ['', Validators.required], // FK Sankhya
        proprietario: ['', Validators.required],
        cpfCnpj: ['', Validators.required],
        cotaParte: [100, [Validators.min(0), Validators.max(100)]],
        dtCompraTransf: [''],
        operador: ['', Validators.required],
        cpfCnpjOperador: ['', Validators.required],
        fabricante: ['', Validators.required],
        anoFabricacao: [''],
        modelo: ['', Validators.required],
        numeroSerie: ['', Validators.required],
        tipoIcao: ['', Validators.maxLength(4)],
        categoriaHomologacao: [''],
        tipoHabilitacaoPilotos: [''],
        classeAeronave: [''],
        classeAeronaveTexto: [''],
        pesoMaximoDecolagem: [''],
        numeroPassageiros: [''],
        tipoVooAutorizado: [''],
        tipoVooAutorizadoTexto: [''],
        tripulacaoMinima: [''],
        numeroAssentos: [''],
        categoriaRegistro: [''],
        numeroMatricula: [''],
        statusOperacao: [''],
        gravame: [''],
        gravameTexto: [''],
        dtValCva: [''],
        sitAeronavegabilidade: [''],
        sitAeronavegabilidadeTexto: [''],
        motivos: [''],
        parceirosVinculados: this.fb.array([])
    });

    get parceirosVinculadosFormArray(): FormArray {
        return this.aeronaveForm.get('parceirosVinculados') as FormArray;
    }

    modalParceiroAberta = false;
    filtroBuscaModal = '';
    statusBuscaParceiro = 'Digite ao menos 2 caracteres para pesquisar...';
    listaParceirosSugeridos = signal<ParceiroSugestao[]>([]);
    
    // Variável para saber qual campo disparou a busca (Proprietário ou Operador)
    tipoBuscaAtiva: 'PROPRIETARIO' | 'OPERADOR' | null = null;
    private buscaParceiroTimer: ReturnType<typeof setTimeout> | null = null;

    abrirModalParceiro(tipo: 'PROPRIETARIO' | 'OPERADOR'): void {
        this.tipoBuscaAtiva = tipo;
        this.filtroBuscaModal = '';
        this.statusBuscaParceiro = 'Digite ao menos 2 caracteres para pesquisar...';
        this.listaParceirosSugeridos.set([]);
        this.modalParceiroAberta = true;
    }

    fecharModalParceiro(): void {
        this.modalParceiroAberta = false;
        this.tipoBuscaAtiva = null;
    }

    agendarBuscaParceirosModal(): void {
        if (this.buscaParceiroTimer) {
            clearTimeout(this.buscaParceiroTimer);
        }

        if (this.filtroBuscaModal.trim().length < 2) {
            this.listaParceirosSugeridos.set([]);
            this.statusBuscaParceiro = 'Digite ao menos 2 caracteres para pesquisar...';
            return;
        }

        this.buscaParceiroTimer = setTimeout(() => {
            this.statusBuscaParceiro = 'Buscando parceiros...';
            // Utiliza o serviço de busca que você forneceu na tela de relatório[cite: 5]
            this.sankhyaGateway.buscarClientesImpressao({ CLIENTE: this.filtroBuscaModal.trim() }).subscribe({
                next: (value: any) => {
                    const body = value?.responseBody || value;
                    const lista = this.normalizarClientes(body?.lista || body?.clientes || []);
                    this.listaParceirosSugeridos.set(lista);
                    this.statusBuscaParceiro = lista.length ? '' : 'Nenhum parceiro localizado.';
                },
                error: () => this.statusBuscaParceiro = 'Erro na comunicação ao buscar parceiros.'
            });
        }, 300);
    }

    private normalizarClientes(lista: any[]): ParceiroSugestao[] {
        if (!Array.isArray(lista)) return [];
        const vistos = new Set<string>();
        const resultado: ParceiroSugestao[] = [];

        for (const item of lista) {
            const codparc = item.codparc || item.CODPARC;
            const nomeparc = item.nomeparc || item.NOMEPARC || item.nomeParc;
            const docparc = item.docparc || item.DOCPARC || item.CNPJ || item.CGC_CPF || '';

            if (!codparc || !nomeparc) continue;
            const chave = docparc.replace(/\D/g, '') || `COD:${codparc}`;

            if (!vistos.has(chave)) {
                vistos.add(chave);
                resultado.push({ codparc, nomeparc, docparc, tipoBusca: 'CLIENTE' });
            }
        }
        return resultado;
    }

    selecionarParceiroModal(parceiro: ParceiroSugestao): void {
        const docLimpo = parceiro.docparc.replace(/\D/g, ''); // Garante envio dos números limpos à máscara
        
        if (this.tipoBuscaAtiva === 'PROPRIETARIO') {
            this.aeronaveForm.patchValue({
                codparc: parceiro.codparc,
                proprietario: parceiro.nomeparc,
                cpfCnpj: docLimpo
            });
            // Força a aplicação da máscara no documento
            this.aplicarMascaraCpfCnpj({ target: { value: docLimpo } }, 'cpfCnpj');
        } else if (this.tipoBuscaAtiva === 'OPERADOR') {
            this.aeronaveForm.patchValue({
                operador: parceiro.nomeparc,
                cpfCnpjOperador: docLimpo
            });
            // Força a aplicação da máscara no documento
            this.aplicarMascaraCpfCnpj({ target: { value: docLimpo } }, 'cpfCnpjOperador');
        }

        this.exibirToast(`Dados de ${this.tipoBuscaAtiva?.toLowerCase()} preenchidos com sucesso!`, 'success');
        this.fecharModalParceiro();
    }

    ngOnInit(): void {
        this.carregarParceirosSankhya();
        const prefixo = (this.prefixoEdicao || '').trim();
        if (prefixo) {
            this.aeronaveForm.get('prefixo')?.setValue(prefixo.toUpperCase(), { emitEvent: false });
            void this.executarBuscaCompletaPrefixo(prefixo);
        }
    }

    async carregarParceirosSankhya(termo: string = ''): Promise<void> {
        try {
            const res: any = await firstValueFrom(this.sankhyaGateway.buscarParceirosAgrupados(termo));
            // O backend já devolve o JSON: [{ docparc: '...', nomeParc: '...', codParcs: ['23624', '23625'] }]
            const lista = res?.lista || res?.responseBody?.parceiro || [];
            this.parceiros.set(lista);
        } catch (err) {
            console.error('Erro ao buscar parceiros agrupados:', err);
        }
    }

    // ==========================================
    // LOGICA AUTOCOMPLETE: PARCEIRO PRINCIPAL
    // ==========================================
    parceirosFiltrados(): any[] {
        const termo = this.filtroParceiro().trim().toLowerCase();
        if (!termo) return this.parceiros();
        return this.parceiros().filter(p => 
            String(p.codParc).includes(termo) || p.nomeParc.toLowerCase().includes(termo)
        );
    }

    onInputParceiro(event: Event): void {
        const input = event.target as HTMLInputElement;
        const valor = input.value;
        this.inputParceiroDisplay.set(valor);

        if (valor.length > 1) {
            this.dropdownParceiroAberto.set(true);
            this.filtroParceiro.set(valor);
            
            if (this.debounceConsulta) clearTimeout(this.debounceConsulta);
            this.debounceConsulta = setTimeout(() => this.carregarParceirosSankhya(valor), 400);
        } else {
            this.dropdownParceiroAberto.set(false);
        }
    }

    selecionarParceiro(item: any): void {
        this.aeronaveForm.get('codparc')?.setValue(item.codParc);
        this.inputParceiroDisplay.set(`${item.codParc} - ${item.nomeParc}`);
        this.dropdownParceiroAberto.set(false);
    }

    fecharDropdownParceiro(): void {
        setTimeout(() => {
            this.dropdownParceiroAberto.set(false);
            if (!this.aeronaveForm.get('codparc')?.value) this.inputParceiroDisplay.set('');
        }, 150);
    }

    valorExibidoParceiro(codigo: any): string {
        if (!codigo) return '';
        const item = this.parceiros().find(p => String(p.codParc) === String(codigo));
        return item ? `${item.codParc} - ${item.nomeParc}` : codigo;
    }

    // ==========================================
    // LOGICA RELACIONAMENTO: PARCEIROS VINCULADOS
    // ==========================================
    criarParceiroVinculado(): FormGroup {
        return this.fb.group({
            cpfCnpj: ['', Validators.required],
            nomeParc: [''],
            codParcs: [[]], // Armazena a lista de CODPARCs irmãos desse documento
            tipoVinculo: ['', Validators.required]
        });
    }

    adicionarParceiroVinculado(): void {
        this.parceirosVinculadosFormArray.push(this.criarParceiroVinculado());
    }

    removerParceiroVinculado(index: number): void {
        const grupo = this.parceirosVinculadosFormArray.at(index);
        const codParcsRemovidos = grupo.get('codParcs')?.value || [];
        const codAeroAtual = this.aeronaveForm.get('codAero')?.value;

        // Ao remover 1 card visual, adiciona TODOS os CODPARCs agrupados na fila de exclusão
        if (codAeroAtual && Array.isArray(codParcsRemovidos)) {
            codParcsRemovidos.forEach(cp => {
                if (cp) this.parceirosVinculadosParaExcluir.push(cp);
            });
        }

        this.parceirosVinculadosFormArray.removeAt(index);
    }

    parceirosFiltradosInline(index: number): any[] {
        const termo = (this.parceirosVinculadosFormArray.at(index).get('codParc')?.value || '').toString().toLowerCase();
        if (!termo) return this.parceiros();
        return this.parceiros().filter(p => 
            String(p.codParc).includes(termo) || p.nomeParc.toLowerCase().includes(termo)
        );
    }

    onInputParceiroVinculado(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const valorDigitado = input.value.trim();

        if (valorDigitado.length > 1) {
            this.dropdownVinculoAberto.set(index);
        } else {
            this.dropdownVinculoAberto.set(-1);
        }

        // Limpa o timer anterior e dispara a busca com o termo digitado
        if (this.debounceConsulta) clearTimeout(this.debounceConsulta);
        this.debounceConsulta = setTimeout(() => {
            this.carregarParceirosSankhya(valorDigitado);
        }, 350);
    }

    valorExibidoParceiroVinculado(formVal: any): string {
        if (!formVal) return '';
        if (formVal.nomeParc) {
            const docFormatado = this.formatarCpfCnpjExibicao(formVal.cpfCnpj);
            return `${formVal.nomeParc} (${docFormatado})`;
        }
        return formVal.cpfCnpj || '';
    }

    async selecionarParceiroInline(item: any, index: number): Promise<void> {
        this.dropdownVinculoAberto.set(-1);
        const docLimpo = String(item.docparc || item.cpfCnpj || item.codParc).replace(/\D/g, '');

        try {
            // Busca todos os CODPARCs cadastrados para esse CPF/CNPJ no Sankhya
            const response: any = await firstValueFrom(this.sankhyaGateway.buscarParceiroPorCpfCnpj(docLimpo));
            const responseBody = response?.responseBody;

            let cods: any[] = [];
            let nome = item.nomeParc || item.nomeparc || '';

            if (responseBody?.sucesso && responseBody?.parceiro) {
                const parc = responseBody.parceiro;
                nome = parc.nomeParceiro || parc.razaoSocial || nome;
                if (Array.isArray(parc.listaEndereco) && parc.listaEndereco.length > 0) {
                    cods = parc.listaEndereco.map((e: any) => e.codparc).filter((c: any) => !!c);
                }
            }

            if (!cods.length) {
                cods = item.codParcs || (item.codParc ? [item.codParc] : []);
            }

            const grupo = this.parceirosVinculadosFormArray.at(index);
            grupo.patchValue({
                cpfCnpj: docLimpo,
                nomeParc: nome,
                codParcs: cods
            });
        } catch (err) {
            console.error('Erro ao buscar CODPARCs do parceiro:', err);
            const grupo = this.parceirosVinculadosFormArray.at(index);
            grupo.patchValue({
                cpfCnpj: docLimpo,
                nomeParc: item.nomeParc || '',
                codParcs: item.codParcs || [item.codParc]
            });
        }
    }

    fecharDropdownVinculo(): void {
        setTimeout(() => this.dropdownVinculoAberto.set(-1), 150);
    }

    // ==========================================
    // LÓGICA DA BUSCA GERAL (INLINE)
    // ==========================================
    agendarBuscaAeronaveGeral(): void {
        if (this.buscaAeronaveGeralTimer) {
            clearTimeout(this.buscaAeronaveGeralTimer);
        }

        const termo = this.filtroBuscaAeronaveGeral.trim().toUpperCase();

        if (termo.length < 2) {
            this.listaAeronavesGeralSugeridas.set([]);
            this.statusBuscaAeronaveGeral.set('Digite ao menos 2 caracteres para pesquisar...');
            this.dropdownBuscaAeronaveGeralAberto = false;
            return;
        }

        this.dropdownBuscaAeronaveGeralAberto = true;

        this.buscaAeronaveGeralTimer = setTimeout(() => {
            this.statusBuscaAeronaveGeral.set('Buscando aeronaves...');

            // Aqui você deve usar o serviço/endpoint que consiga pesquisar e retornar o PROPRIETARIO na lista.
            // Exemplo usando uma função no seu SankhyaGateway:
            this.sankhyaGateway.buscarAeronavesComProprietario(termo).subscribe({
                next: (value: any) => {
                    const body = value?.responseBody || value;
                    const result = body?.result || body?.listaAeronave || [];

                    // Normaliza os dados para a lista. Certifique-se que o backend retorna o Proprietário.
                    const listaSugerida = result.map((row: any) => ({
                        CODAERO: row.CODAERO || row[0],
                        PREFIXO: row.PREFIXO || row[1],
                        PROPRIETARIO: row.PROPRIETARIO || row[2] || 'Não informado'
                    }));

                    this.listaAeronavesGeralSugeridas.set(listaSugerida);
                    this.statusBuscaAeronaveGeral.set(listaSugerida.length ? '' : 'Nenhuma aeronave localizada.');
                },
                error: () => this.statusBuscaAeronaveGeral.set('Erro na comunicação ao buscar aeronaves.')
            });
        }, 300);
    }

    async selecionarAeronaveGeral(aeronave: any): Promise<void> {
        // Fecha o dropdown e recolhe o card de busca
        this.dropdownBuscaAeronaveGeralAberto = false;
        this.buscaColapsada = true;

        // Define o prefixo no input visualmente, se desejar
        this.aeronaveForm.get('prefixo')?.setValue(aeronave.PREFIXO, { emitEvent: false });

        // Chama a função que já gerencia todo o fluxo (Sankhya -> ANAC -> Preenchimento)
        // Note que como executarBuscaCompletaPrefixo é privado, se o TypeScript reclamar,
        // basta usá-lo como já está na classe ou chamá-lo diretamente.
        this.executarBuscaCompletaPrefixo(aeronave.PREFIXO);
    }

    // ==========================================
    // SALVAMENTO & INTEGRAÇÃO SANKHYA
    // ==========================================
    async salvarAeronave(): Promise<void> {
        if (this.aeronaveForm.invalid) {
            this.aeronaveForm.markAllAsTouched();
            this.exibirToast('Por favor, preencha os campos obrigatórios.', 'warning');
            return;
        }

        this.salvando.set(true);

        try {
            const formValue = this.aeronaveForm.value;

            // Tratamento para inputs customizados (Se for "Outro", grava o valor textual digitado)
            const classeFinal = formValue.classeAeronave === 'Outro' ? this.removerAcentos(formValue.classeAeronaveTexto) : formValue.classeAeronave;
            const vooFinal = formValue.tipoVooAutorizado === 'Outro' ? this.removerAcentos(formValue.tipoVooAutorizadoTexto) : formValue.tipoVooAutorizado;
            const gravameFinal = formValue.gravame === 'Outro' ? this.removerAcentos(formValue.gravameTexto) : formValue.gravame;
            const sitFinal = formValue.sitAeronavegabilidade === 'Outro' ? this.removerAcentos(formValue.sitAeronavegabilidadeTexto) : formValue.sitAeronavegabilidade;

            const values: any = {
                '1': formValue.prefixo.toUpperCase(),
                '2': formValue.codparc,
                '3': formValue.proprietario.toUpperCase(),
                '4': String(formValue.cpfCnpj).replace(/\D/g, ''),
                '5': formValue.cotaParte,
                '6': formValue.dtCompraTransf ? this.formatarData(formValue.dtCompraTransf) : '',
                '7': formValue.operador.toUpperCase(),
                '8': String(formValue.cpfCnpjOperador).replace(/\D/g, ''),
                '9': formValue.fabricante.toUpperCase(),
                '10': formValue.anoFabricacao,
                '11': formValue.modelo.toUpperCase(),
                '12': formValue.numeroSerie.toUpperCase(),
                '13': formValue.tipoIcao.toUpperCase(),
                '14': formValue.categoriaHomologacao,
                '15': formValue.tipoHabilitacaoPilotos.toUpperCase(),
                '16': classeFinal,
                '17': formValue.pesoMaximoDecolagem,
                '18': formValue.numeroPassageiros,
                '19': vooFinal,
                '20': formValue.tripulacaoMinima,
                '21': formValue.numeroAssentos,
                '22': formValue.categoriaRegistro.toUpperCase(),
                '23': formValue.numeroMatricula,
                '24': formValue.statusOperacao,
                '25': gravameFinal,
                '26': formValue.dtValCva ? this.formatarData(formValue.dtValCva) : '',
                '27': sitFinal,
                '28': formValue.motivos
            };

            const recordItem: any = {
                values: values
            };

            if (formValue.codAero) {
                recordItem.pk = {
                    CODAERO: formValue.codAero
                };
            }

            // Montagem do payload nativo para a tabela AD_TCADAERO
            const payloadAeronave = {
                entityName: 'AD_TCADAERO',
                standAlone: false,
                fields: [
                    'CODAERO',                // 0
                    'PREFIXO',                // 1
                    'CODPARC',                // 2
                    'PROPRIETARIO',           // 3
                    'CPFCNPJ',                // 4 (Corrigido)
                    'COTAPARTE',              // 5
                    'DTCOMPRATRANSF',         // 6
                    'OPERADOR',               // 7
                    'CPFCNPJOPERADOR',        // 8 (Corrigido)
                    'FABRICANTE',             // 9
                    'ANOFABRICACAO',          // 10
                    'MODELO',                 // 11
                    'NUMEROSERIE',            // 12
                    'TIPOICAO',               // 13
                    'CATEGORIAHOMOLOGACAO',   // 14
                    'TIPOHABILITACAOPILOTOS', // 15
                    'CLASSEAERONAVE',         // 16
                    'PESOMAXIMODECOLAGEM',    // 17
                    'NUMEROPASSAGEIROS',      // 18
                    'TIPOVOOAUTORIZADO',      // 19
                    'TRIPULACAOMINIMA',       // 20
                    'NUMEROASSENTOS',         // 21
                    'CATEGORIAREGISTRO',      // 22
                    'NUMEROMATRICULA',        // 23
                    'STATUSOPERACAO',         // 24
                    'GRAVAME',                // 25
                    'DTVALCVA',               // 26
                    'SITAERONAVEGABILIDADE',  // 27
                    'MOTIVOS'                 // 28
                ],
                records: [
                    recordItem
                ]
            };

            console.log(JSON.stringify(payloadAeronave, null, 2));

            const resSalvar: any = await firstValueFrom(this.sankhyaGateway.salvarSankhyaPorEntidade(payloadAeronave));
            if (resSalvar.status === '3') {
                this.exibirToast('Erro ao salvar aeronave no Sankhya: favor deslogar e logar novamente', 'danger');
                return;
            }
            const codAero = resSalvar?.responseBody?.result?.[0]?.[0];

            if (codAero) {
                if (this.parceirosVinculadosParaExcluir.length > 0) {
                    await this.excluirVinculosRemovidosSankhya(codAero);
                }

                // Se salvou a aeronave principal, salva o FormArray dinâmico de Co-proprietários na tabela relacional AD_AEROPAR
                const vinculos = formValue.parceirosVinculados || [];
                for (const v of vinculos) {
                    const listCodParcs = Array.isArray(v.codParcs) && v.codParcs.length > 0 ? v.codParcs : [v.cpfCnpj];
                    for (const codP of listCodParcs) {
                        if (!codP) continue;
                        const payloadVinculo = {
                            entityName: 'AD_AEROPAR',
                            standAlone: false,
                            fields: ['CODAERO', 'CODPARC', 'TIPO_VINCULO'],
                            records: [{
                                values: {
                                    '0': codAero,
                                    '1': codP,
                                    '2': v.tipoVinculo
                                }
                            }]
                        };
                        await firstValueFrom(this.sankhyaGateway.salvarSankhyaPorEntidade(payloadVinculo));
                    }
                }

                // Espelha no Aerosys (AERONAVE + proprietários). Best-effort: já está gravada no Sankhya.
                let msgAerosys = '';
                try {
                    const resAero: any = await firstValueFrom(this.sankhyaGateway.espelharAeronaveAerosys({
                        matricula:     String(formValue.prefixo || '').toUpperCase(),
                        numSerie:      formValue.numeroSerie,
                        anoFab:        formValue.anoFabricacao,
                        modelo:        formValue.modelo,
                        fabricante:    formValue.fabricante,
                        categHomo:     formValue.categoriaHomologacao,
                        categReg:      formValue.categoriaRegistro,
                        tripMin:       formValue.tripulacaoMinima,
                        maxAssentoPax: formValue.numeroPassageiros,
                        maxAssento:    formValue.numeroAssentos,
                        pesoMax:       formValue.pesoMaximoDecolagem,
                        tipoVoo:       vooFinal,
                        validadeCa:    formValue.dtValCva || null,
                        obs:           formValue.motivos,
                        proprietario:  { codparc: Number(formValue.codparc) || 0, dataContrato: formValue.dtCompraTransf || null },
                        vinculados:    (formValue.parceirosVinculados || []).map((v: any) => ({
                            tipoVinculo: v.tipoVinculo,
                            codParcs:    Array.isArray(v.codParcs) ? v.codParcs.map((c: any) => Number(c)).filter((c: number) => c > 0) : []
                        }))
                    }));
                    if (!resAero?.sucesso) {
                        msgAerosys = ' Aerosys: ' + (resAero?.erro || 'falha ao gravar a aeronave.');
                    } else if (resAero?.avisos?.length) {
                        msgAerosys = ' Aerosys: ' + resAero.avisos.join(' ');
                    }
                } catch (eAero: any) {
                    console.error('[Aeronave] espelho Aerosys falhou', eAero);
                    msgAerosys = ' Aerosys: não foi possível gravar (' + (eAero?.error?.message || eAero?.message || 'erro') + ').';
                }

                this.exibirToast('Aeronave e vínculos de parceiros cadastrados com sucesso!' + msgAerosys, msgAerosys ? 'warning' : 'success');
                // Sem avisos: volta para a lista depois de mostrar a confirmacao.
                if (!msgAerosys) setTimeout(() => this.salvo.emit(), 1200);
                this.aeronaveForm.reset();
                this.inputParceiroDisplay.set('');
                while (this.parceirosVinculadosFormArray.length) this.parceirosVinculadosFormArray.removeAt(0);
            }
        } catch (err) {
            console.error(err);
            this.exibirToast('Erro ao salvar aeronave no Sankhya.', 'danger');
        } finally {
            this.salvando.set(false);
        }
    }

    // ==========================================
    // MÁSCARAS E AUXILIARES
    // ==========================================
    aplicarMascaraCpfCnpj(event: any, campo: string): void {
        let valor = event.target.value.replace(/\D/g, '');
        if (valor.length <= 11) {
            valor = valor.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            valor = valor.substring(0, 14).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
        }
        this.aeronaveForm.get(campo)?.setValue(valor, { emitEvent: false });
    }

    exibirToast(mensagem: string, tipo: 'success' | 'danger' | 'warning' | 'info' = 'success') {
        this.toast.set({ mensagem, tipo });
        setTimeout(() => this.toast.set(null), tipo === 'warning' ? 9000 : 4000);
    }

    onKeyDownNavegacao(event: KeyboardEvent, contexto: 'PARCEIRO'): void {
        if (!this.dropdownParceiroAberto()) return;
        const lista = this.parceirosFiltrados();
        const foco = this.indiceFocoAtual();

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i < lista.length - 1 ? i + 1 : i);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i > 0 ? i - 1 : 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (foco >= 0 && foco < lista.length) this.selecionarParceiro(lista[foco]);
            this.indiceFocoAtual.set(-1);
        } else if (event.key === 'Escape') {
            this.fecharDropdownParceiro();
            this.indiceFocoAtual.set(-1);
        }
    }

    onKeyDownNavegacaoVinculo(event: KeyboardEvent, index: number): void {
        if (this.dropdownVinculoAberto() !== index) return;
        const lista = this.parceirosFiltradosInline(index);
        const foco = this.indiceFocoAtual();

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i < lista.length - 1 ? i + 1 : i);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i > 0 ? i - 1 : 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (foco >= 0 && foco < lista.length) this.selecionarParceiroInline(lista[foco], index);
            this.indiceFocoAtual.set(-1);
        } else if (event.key === 'Escape') {
            this.fecharDropdownVinculo();
            this.indiceFocoAtual.set(-1);
        }
    }

    private limparFormularioParcial(): void {
        const prefixo = this.aeronaveForm.get('prefixo')?.value;
        const ultimo = this.ultimoPrefixoConsultado;

        // Salva os valores que não devem ser limpos
        const valoresPreservados = {
            prefixo: prefixo
        };

        this.limparFormulario();
        this.aeronaveForm.patchValue(valoresPreservados, { emitEvent: false });
        this.ultimoPrefixoConsultado = ultimo;
    }

    /**
     * Limpa completamente o formulário de cadastro de aeronaves,
     * reiniciando estados visuais, arrays de vínculos e variáveis de controle.
     */
    limparFormulario(): void {
        // 1. Reseta os valores dos campos do ReactiveForm para os valores padrão
        this.aeronaveForm.reset({
            codAero: null,
            prefixo: '',
            codparc: '',
            proprietario: '',
            cpfCnpj: '',
            cotaParte: 100,
            dtCompraTransf: '',
            operador: '',
            cpfCnpjOperador: '',
            fabricante: '',
            anoFabricacao: '',
            modelo: '',
            numeroSerie: '',
            tipoIcao: '',
            categoriaHomologacao: '',
            tipoHabilitacaoPilotos: '',
            classeAeronave: '',
            classeAeronaveTexto: '',
            pesoMaximoDecolagem: '',
            numeroPassageiros: '',
            tipoVooAutorizado: '',
            tipoVooAutorizadoTexto: '',
            tripulacaoMinima: '',
            numeroAssentos: '',
            categoriaRegistro: '',
            numeroMatricula: '',
            statusOperacao: '',
            gravame: '',
            gravameTexto: '',
            dtValCva: '',
            sitAeronavegabilidade: '',
            sitAeronavegabilidadeTexto: '',
            motivos: ''
        });

        // 2. Esvazia o FormArray de parceiros vinculados (AD_AEROPAR)
        while (this.parceirosVinculadosFormArray.length !== 0) {
            this.parceirosVinculadosFormArray.removeAt(0);
        }

        // 3. Reseta o array de controle para exclusões no Sankhya
        this.parceirosVinculadosParaExcluir = [];

        // 4. Reseta os Sinais de controle visual e autocompletes
        this.inputParceiroDisplay.set('');
        this.filtroParceiro.set('');
        this.dropdownParceiroAberto.set(false);
        this.dropdownVinculoAberto.set(-1);
        this.indiceFocoAtual.set(-1);
        this.buscandoPrefixo.set(false);
        this.dropdownPrefixoAberto.set(false);
        this.sugestoesPrefixo.set([]);
        this.buscandoPrefixo.set(false);
        this.ultimoPrefixoConsultado = '';

        this.filtroBuscaAeronaveGeral = '';
        this.dropdownBuscaAeronaveGeralAberto = false;
        this.buscaColapsada = false;
        this.listaAeronavesGeralSugeridas.set([]);
        this.statusBuscaAeronaveGeral.set('Digite ao menos 2 caracteres para pesquisar...');
    }

    // ==========================================
    // CONSULTA AUTOMÁTICA DE AERONAVE POR PREFIXO
    // ==========================================

    async onPrefixoBlur() {
        const prefixoControl = this.aeronaveForm.get('prefixo');
        let prefixo = prefixoControl?.value;

        if (!prefixo || prefixoControl?.invalid) return;

        this.limparFormularioParcial();

        // Normaliza o prefixo (Ex: PT-ABC ou PTABC)
        prefixo = prefixo.trim().toUpperCase();
        prefixoControl?.setValue(prefixo, { emitEvent: false });

        this.buscandoPrefixo.set(true);

        try {
            // 1. TENTA BUSCAR NO SANKHYA PRIMEIRO
            const response: any = await firstValueFrom(this.sankhyaGateway.buscarAeronavePorPrefixo(prefixo));
            const aeronaveSankhya = response?.responseBody?.aeronave;

            if (aeronaveSankhya && aeronaveSankhya.CODAERO) {
                // Se encontrou no Sankhya, preenche com os dados internos
                this._preencherFormularioComAeronavePropria(aeronaveSankhya);
                this.exibirToast('Aeronave e vínculos carregados do Sankhya.', 'success');
            } else {
                if (response.status === '3') {
                    this.exibirToast('Erro ao consultar aeronave no Sankhya: favor deslogar e logar novamente', 'danger');
                    return;
                }
                // 2. SE NÃO ENCONTROU NO SANKHYA, CONSULTA NA ANAC
                this.exibirToast('Aeronave não localizada no Sankhya. Consultando dados na ANAC...', 'info');
                await this.buscarDadosAnac(prefixo);
            }
        } catch (err) {
            console.error('Erro na consulta por prefixo:', err);
            // Em caso de falha no Sankhya, tenta buscar na ANAC como fallback
            await this.buscarDadosAnac(prefixo);
        } finally {
            this.buscandoPrefixo.set(false);
        }
    }

    onInputPrefixo(event: Event): void {
        const input = event.target as HTMLInputElement;
        const valor = input.value.trim().toUpperCase();

        if (this.debouncePrefixoTimer) clearTimeout(this.debouncePrefixoTimer);

        if (valor.length < 2) {
            this.dropdownPrefixoAberto.set(false);
            this.sugestoesPrefixo.set([]);
            return;
        }

        this.buscandoPrefixo.set(true);

        this.debouncePrefixoTimer = setTimeout(async () => {
            try {
                // Utiliza a função buscarAeronaves que já existe no seu SankhyaGateway
                const res: any = await firstValueFrom(this.sankhyaGateway.buscarAeronaves(valor));
                const result = res?.responseBody?.result || [];

                const formatados = result.map((row: any[]) => ({
                    CODAERO: row[0],
                    PREFIXO: row[1]
                }));

                this.sugestoesPrefixo.set(formatados);
                this.dropdownPrefixoAberto.set(true);
            } catch (err) {
                console.error('Erro na consulta em tempo real de prefixos:', err);
            } finally {
                this.buscandoPrefixo.set(false);
            }
        }, 350);
    }

    async selecionarAeronaveSankhya(item: any): Promise<void> {
        this.dropdownPrefixoAberto.set(false);
        this.aeronaveForm.get('prefixo')?.setValue(item.PREFIXO, { emitEvent: false });
        this.buscandoPrefixo.set(true);
        this.ultimoPrefixoConsultado = item.PREFIXO;

        try {
            const response: any = await firstValueFrom(this.sankhyaGateway.buscarAeronavePorPrefixo(item.PREFIXO));
            const aeronaveSankhya = response?.responseBody?.aeronave;

            if (aeronaveSankhya && aeronaveSankhya.CODAERO) {
                this._preencherFormularioComAeronavePropria(aeronaveSankhya);
                this.exibirToast('Aeronave e vínculos carregados do Sankhya.', 'success');
            } else {
                await this.buscarDadosAnac(item.PREFIXO);
            }
        } catch (err) {
            console.error('Erro ao selecionar aeronave da lista:', err);
            await this.buscarDadosAnac(item.PREFIXO);
        } finally {
            this.buscandoPrefixo.set(false);
        }
    }

    fecharDropdownPrefixo(): void {
        setTimeout(() => {
            this.dropdownPrefixoAberto.set(false);

            const prefixoVal = this.aeronaveForm.get('prefixo')?.value;

            // Se saiu do campo, tem algo digitado, não está processando uma busca e não tem ID (codAero) de uma aeronave salva
            if (prefixoVal && !this.buscandoPrefixo()) {
                this.executarBuscaCompletaPrefixo(prefixoVal);
            }
        }, 200);
    }

    cadastrarNovoPrefixo(): void {
        this.dropdownPrefixoAberto.set(false);
        const prefixoVal = this.aeronaveForm.get('prefixo')?.value;

        // Evita chamadas duplicadas caso o "Enter" e o "Blur" ocorram quase ao mesmo tempo
        if (prefixoVal && !this.buscandoPrefixo()) {
            this.executarBuscaCompletaPrefixo(prefixoVal);
        }
    }

    onKeyDownNavegacaoPrefixo(event: KeyboardEvent): void {
        if (!this.dropdownPrefixoAberto()) return;
        const lista = this.sugestoesPrefixo();
        const foco = this.indiceFocoAtual();

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i < lista.length - 1 ? i + 1 : i);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.indiceFocoAtual.update(i => i > 0 ? i - 1 : 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();

            // Se há sugestões e o usuário usou a seta para focar em uma, seleciona a do Sankhya
            if (lista.length > 0 && foco >= 0 && foco < lista.length) {
                this.selecionarAeronaveSankhya(lista[foco]);
            } else {
                // Se a lista estiver vazia OU o usuário não focou em nada e só deu Enter, busca o novo prefixo
                this.cadastrarNovoPrefixo();
            }
            this.indiceFocoAtual.set(-1);
        } else if (event.key === 'Escape') {
            this.dropdownPrefixoAberto.set(false);
            this.indiceFocoAtual.set(-1);
        }
    }

    private async executarBuscaCompletaPrefixo(prefixo: string) {
        const prefixoControl = this.aeronaveForm.get('prefixo');
        if (!prefixo || prefixoControl?.invalid) return;
        prefixo = prefixo.trim().toUpperCase();

        if (prefixo === this.ultimoPrefixoConsultado) return;

        this.limparFormularioParcial();

        this.ultimoPrefixoConsultado = prefixo;
        prefixoControl?.setValue(prefixo, { emitEvent: false });
        this.buscandoPrefixo.set(true);

        try {
            const response: any = await firstValueFrom(this.sankhyaGateway.buscarAeronavePorPrefixo(prefixo));
            const aeronaveSankhya = response?.responseBody?.aeronave;

            if (aeronaveSankhya && aeronaveSankhya.CODAERO) {
                this._preencherFormularioComAeronavePropria(aeronaveSankhya);
                this.exibirToast('Aeronave e vínculos carregados do Sankhya.', 'success');
            } else {
                if (response.status === '3') {
                    this.exibirToast('Erro ao consultar aeronave no Sankhya: favor deslogar e logar novamente', 'danger');
                    return;
                }
                this.exibirToast('Aeronave não localizada no Sankhya. Consultando dados na ANAC...', 'info');
                await this.buscarDadosAnac(prefixo);
            }
        } catch (err) {
            console.error('Erro na consulta por prefixo:', err);
            await this.buscarDadosAnac(prefixo);
        } finally {
            this.buscandoPrefixo.set(false);
        }
    }

    private _preencherFormularioComAeronavePropria(aeronave: any): void {
        this.parceirosVinculadosParaExcluir = [];
        this.aeronaveForm.patchValue({
            codAero: aeronave.CODAERO || null,
            codparc: aeronave.CODPARC || '',
            proprietario: aeronave.PROPRIETARIO || '',
            cpfCnpj: aeronave.CPFCNPJ || '',
            cotaParte: aeronave.COTAPARTE || 100,
            dtCompraTransf: aeronave.DTCOMPRATRANSF || '',
            operador: aeronave.OPERADOR || '',
            cpfCnpjOperador: aeronave.CPFCNPJOPERADOR || '',
            fabricante: aeronave.FABRICANTE || '',
            anoFabricacao: aeronave.ANOFABRICACAO || '',
            modelo: aeronave.MODELO || '',
            numeroSerie: aeronave.NUMEROSERIE || '',
            tipoIcao: aeronave.TIPOICAO || '',
            categoriaHomologacao: aeronave.CATEGORIAHOMOLOGACAO || '',
            tipoHabilitacaoPilotos: aeronave.TIPOHABILITACAOPILOTOS || '',
            classeAeronave: aeronave.CLASSEAERONAVE || '',
            pesoMaximoDecolagem: aeronave.PESOMAXIMODECOLAGEM || '',
            numeroPassageiros: aeronave.NUMEROPASSAGEIROS || '',
            tipoVooAutorizado: aeronave.TIPOVOOAUTORIZADO || '',
            tripulacaoMinima: aeronave.TRIPULACAOMINIMA || '',
            numeroAssentos: aeronave.NUMEROASSENTOS || '',
            categoriaRegistro: aeronave.CATEGORIAREGISTRO || '',
            numeroMatricula: aeronave.NUMEROMATRICULA || '',
            statusOperacao: aeronave.STATUSOPERACAO || '',
            gravame: aeronave.GRAVAME || '',
            dtValCva: aeronave.DTVALCVA || '',
            sitAeronavegabilidade: aeronave.SITAERONAVEGABILIDADE || '',
            motivos: aeronave.MOTIVOS || ''
        }, { emitEvent: false });

        if (aeronave.CODPARC && aeronave.PROPRIETARIO) {
            this.inputParceiroDisplay.set(`${aeronave.CODPARC} - ${aeronave.PROPRIETARIO}`);
        }

        if (aeronave.CGC_CPF) this.aplicarMascaraCpfCnpj({ target: { value: aeronave.CGC_CPF } }, 'cpfCnpj');
        if (aeronave.CGC_CPF_OPERADOR) this.aplicarMascaraCpfCnpj({ target: { value: aeronave.CGC_CPF_OPERADOR } }, 'cpfCnpjOperador');

        // Agrupa os CODPARCs retornados do banco pelo mesmo CPF/CNPJ em 1 Card visual por pessoa
        while (this.parceirosVinculadosFormArray.length) {
            this.parceirosVinculadosFormArray.removeAt(0);
        }

        if (aeronave.parceirosVinculados && aeronave.parceirosVinculados.length > 0) {
            const mapaAgrupado = new Map<string, { cpfCnpj: string, nomeParc: string, codParcs: any[], tipoVinculo: string }>();

            aeronave.parceirosVinculados.forEach((vinculo: any) => {
                const docPuro = String(vinculo.cpfCnpj || vinculo.CGC_CPF || vinculo.CPFCNPJ || vinculo.codParc || '').replace(/\D/g, '');
                const key = docPuro || `COD_${vinculo.codParc}`;

                if (mapaAgrupado.has(key)) {
                    const existente = mapaAgrupado.get(key)!;
                    if (vinculo.codParc && !existente.codParcs.includes(vinculo.codParc)) {
                        existente.codParcs.push(vinculo.codParc);
                    }
                } else {
                    mapaAgrupado.set(key, {
                        cpfCnpj: docPuro,
                        nomeParc: vinculo.nomeParc || vinculo.NOMEPARC || `Parceiro ${docPuro}`,
                        codParcs: vinculo.codParc ? [vinculo.codParc] : [],
                        tipoVinculo: vinculo.tipoVinculo || vinculo.TIPO_VINCULO || ''
                    });
                }
            });

            mapaAgrupado.forEach((item) => {
                const grupo = this.criarParceiroVinculado();
                grupo.patchValue({
                    cpfCnpj: item.cpfCnpj,
                    nomeParc: item.nomeParc,
                    codParcs: item.codParcs,
                    tipoVinculo: item.tipoVinculo
                });
                this.parceirosVinculadosFormArray.push(grupo);
            });
        }
    }

    private async excluirVinculosRemovidosSankhya(codAero: any): Promise<void> {
        for (const codParc of this.parceirosVinculadosParaExcluir) {
            try {
                const payload = {
                    dataSetID: "00Z", // Padrão genérico Sankhya
                    entityName: "AD_AEROPAR",
                    standAlone: false,
                    // A chave composta da tabela relacional
                    pks: [
                        { CODAERO: codAero, CODPARC: codParc }
                    ]
                };
                
                await firstValueFrom(this.sankhyaGateway.proxy('mge', 'DatasetSP.removeRecord', payload));
                console.log(`Vínculo excluído com sucesso: Aero ${codAero} - Parceiro ${codParc}`);
                
            } catch (e) {
                console.error(`Erro ao tentar excluir o vínculo CODPARC ${codParc} no Sankhya:`, e);
                this.exibirToast(`Não foi possível excluir um dos parceiros vinculados.`, 'warning');
            }
        }
    }

    private removerAcentos(texto: string): string {
        if (!texto) return '';
        return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    private formatarData(data: string): string {
        if (!data) return '';
        const partes = data.split('-');
        if (partes.length === 3) {
            return `${partes[2]}/${partes[1]}/${partes[0]}`;
        }
        return data;
    }

    /**
     * Método responsável por consultar os dados diretamente da ANAC/Backend
     */
    private async buscarDadosAnac(prefixo: string): Promise<void> {
        try {
            // O ideal é chamar um endpoint backend (ex: seu SankhyaGateway ou proxy Java)
            // que fará o Scraping/GET na URL https://aeronaves.anac.gov.br/aeronaves/cons_rab.asp?tipo_pesquisa=marcas&textMarca=PREFIXO
            const resAnac: any = await firstValueFrom(this.sankhyaGateway.buscarAeronaveAnac(prefixo));

            if (resAnac && resAnac.sucesso) {
                const dadosAnac = resAnac.dados || {};
                // 1. Extrai dados do Proprietário e Operador
                const propNome = (dadosAnac['Proprietário - Proprietário'] || '').toUpperCase();
                const propCpfCnpjBruto = dadosAnac['Proprietário - CPF/CNPJ'] || '';

                // 2. Variáveis de controle para saber se foram encontrados no seu sistema
                let proprietario: any;
                let mensagensAviso: string[] = [];

                // 3. Valida Proprietário no cadastro do sistema (usando CPF/CNPJ se disponível)
                if (propCpfCnpjBruto || propNome) {// Verifica se o CPF do proprietário veio mascarado (contém '*')
                    const estaMascarado = propCpfCnpjBruto.includes('*');
                    
                    // Limpa pontuação para o caso de vir um CPF/CNPJ completo não mascarado
                    const documentoLimpo = propCpfCnpjBruto.replace(/\D/g, '');

                    // Se estiver mascarado, busca pelo NOME. Se não estiver, usa o DOCUMENTO (limpo ou original)
                    const termoBuscaProp = estaMascarado ? propNome : (documentoLimpo || propCpfCnpjBruto);

                    proprietario = await firstValueFrom(this.sankhyaGateway.buscarParceiros(termoBuscaProp));

                    if (proprietario.status === '1') {
                        if (proprietario.responseBody?.result?.length === 0) {
                            dadosAnac.proprietarioExiste = false;
                            mensagensAviso.push(`O Proprietário "${propNome}" não foi encontrado no cadastro do sistema.`);
                        } else {
                            dadosAnac['codparc'] = proprietario.responseBody.result[0][0];
                            dadosAnac.proprietarioExiste = true;
                        }
                    } else {
                        if (proprietario.status === '3') {
                            this.exibirToast('Erro ao consultar proprietário no Sankhya: favor deslogar e logar novamente', 'danger');
                            return;
                        }
                        dadosAnac.proprietarioExiste = false;
                        mensagensAviso.push(`O Proprietário "${propNome}" não foi encontrado no cadastro do sistema.`);
                    }
                }

                this._preencherFormularioComDadosAnac(dadosAnac);
                if (mensagensAviso.length > 0) {
                    this.exibirToast('Atenção:\n\n' + mensagensAviso.join('\n'), 'warning');
                } else {
                    this.exibirToast('Dados preenchidos com sucesso via ANAC!', 'success');
                }
            } else {
                this.exibirToast('Aeronave não encontrada no registro da ANAC.', 'warning');
            }
        } catch (anacErr) {
            console.error('Erro ao consultar site da ANAC:', anacErr);
            this.exibirToast('Não foi possível obter dados da ANAC para este prefixo.', 'danger');
        }
    }

    /**
     * Preenche e mapeia os campos retornados do scraping da ANAC para a tela
     */
    private _preencherFormularioComDadosAnac(dadosAnac: any): void {
        if (!dadosAnac) return;

        const gravameBruto = dadosAnac['GRAVAME'] || '';
        const gravameFormatado = gravameBruto.replace(/^\d+\s*-\s*/, '').trim();

        const pesoBruto = dadosAnac['Peso Máximo de Decolagem'] || '';
        const pesoNumerico = this.extrairNumeroDecimal(pesoBruto);

        const sitAeronavegabilidadeBruta = dadosAnac['Situação da Aeronave'] || '';
        const sitAeronavegabilidadeFormatada = this.removerAcentos(sitAeronavegabilidadeBruta);

        this.aeronaveForm.patchValue({
            // Proprietário
            proprietario: dadosAnac.proprietarioExiste ? dadosAnac['Proprietário - Proprietário'] : '',
            cpfCnpj: dadosAnac.proprietarioExiste ? dadosAnac['Proprietário - CPF/CNPJ'] : '',
            codparc: dadosAnac.proprietarioExiste ? dadosAnac['codparc'] : '',

            // Operador
            operador: dadosAnac['Operador'] || '',
            cpfCnpjOperador: dadosAnac['CPF/CNPJ'] || '',

            // Dados Técnicos da Aeronave
            fabricante: dadosAnac['Fabricante'] || '',
            anoFabricacao: dadosAnac['Ano de Fabricação'] || '',
            modelo: dadosAnac['Modelo'] || '',
            numeroSerie: dadosAnac['Número de Série'] || '',
            tipoIcao: dadosAnac['Tipo ICAO'] || '',
            categoriaHomologacao: dadosAnac['Categoria de Certificação do modelo da aeronave']?.toUpperCase() || '',
            tipoHabilitacaoPilotos: dadosAnac['Tipo de Habilitação para Pilotos'] || '',
            classeAeronave: dadosAnac['Classe da Aeronave'] || '',
            pesoMaximoDecolagem: pesoNumerico,
            numeroPassageiros: dadosAnac['Número de Passageiros'] || '',
            tipoVooAutorizado: dadosAnac['Tipo de Voo Autorizado'] || '',
            tripulacaoMinima: dadosAnac['Tripulação mínima prevista na certificação'] || '',
            numeroAssentos: dadosAnac['Número de Assentos'] || '',
            categoriaRegistro: dadosAnac['Tipo de Certificado de Aeronavegabilidade'] || '',
            numeroMatricula: dadosAnac['Marca / Prefixo'] || '',
            statusOperacao: dadosAnac['Tipo do Operador'] || '',
            gravame: gravameFormatado || '',
            dtValCva: dadosAnac['Data de Validade do CVA'] || '',
            sitAeronavegabilidade: sitAeronavegabilidadeFormatada,
            motivos: dadosAnac['Motivos'] || ''
        }, { emitEvent: false });

        // Aplica as máscaras nos campos de CPF/CNPJ obtidos
        if (dadosAnac.cpfCnpjProprietario) {
            this.aplicarMascaraCpfCnpj({ target: { value: dadosAnac.cpfCnpjProprietario } }, 'cpfCnpj');
        }
        if (dadosAnac.cpfCnpjOperador) {
            this.aplicarMascaraCpfCnpj({ target: { value: dadosAnac.cpfCnpjOperador } }, 'cpfCnpjOperador');
        }
    }

    private extrairNumeroDecimal(texto: string): number | null {
        if (!texto) return null;
        
        const apenasNumeros = texto.replace('.', '').replace(',', '.').replace(/[^0-9.]/g, '');
        
        const valor = parseFloat(apenasNumeros);
        return isNaN(valor) ? null : valor;
    }

    formatarCpfCnpjExibicao(valor: any): string {
        if (!valor) return 'Não informado';
        let doc = String(valor).replace(/\D/g, '');
        if (doc.length <= 11) {
            doc = doc.padStart(11, '0');
            return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else {
            doc = doc.padStart(14, '0');
            return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
    }
}